import React, { useMemo, useState } from 'react';

import { CustomSelect } from '../../../../components';
import { EChallengesType, ECriterion, EPillar } from '../../../../types/challenges';
import CustomInput from './CustomInput';
import CustomSwitch from './CustomSwitch';
import CustomTextarea from './CustomTextarea';

interface Option {
  label: string;
  value: string;
}

type FormType = {
  name: string;
  description: string;
  criterion: ECriterion | '';
  criterionValue: number;
  criterionSecondValue: number;
  pillar: EPillar | '';
  variableId: number;
  points: number;
  uniqueExecute: boolean;
  active: boolean;
  validUntil: string;
  type: EChallengesType | '';
};

interface EditFormProps {
  form: FormType;
  onFormChange: (field: keyof FormType, value: any) => void;
  criterionOptions: Option[];
  pillarOptions: Option[];
  variableOptions: Option[];
  onBack: () => void;
  onSubmit: () => void;
}

type VariableRule = {
  descriptor: string;
  unit: string;
  help: string;
};

// Layout por variável (label do "Valor", unidade e texto de ajuda) espelhando o
// comportamento do backend em ChallengeDriverServiceImpl.evaluateChallengeVariable.
// Chave = nome da variável (VariableEntity.name).
const VARIABLE_RULES: Record<string, VariableRule> = {
  ETA: {
    descriptor: 'Em Minutos',
    unit: 'Min',
    help: 'Chegar no destino dentro de uma tolerância permitida. Medido em minutos.',
  },
  'Check-out': {
    descriptor: 'Em metros',
    unit: 'M',
    help: 'Check-out: Verifica se o motorista chegou no destino da viagem dentro de um raio permitido. Medido em metros.',
  },
  'Check-in': {
    descriptor: 'Em metros',
    unit: 'M',
    help: 'Check-in: Verifica se o motorista iniciou na origem da viagem dentro de um raio permitido. Medido em metros.',
  },
  'Velocidade Indicada': {
    descriptor: 'Em porcentagem',
    unit: '%',
    help: 'Velocidade Indicada: Permanecer na velocidade recomendada pela via dentro de uma tolerância permitida. Medido em percentual',
  },
  'Distância Percorrida': {
    descriptor: 'Em Km percorrido',
    unit: 'Km',
    help: 'Distância Percorrida: Quantidade de km percorridos.',
  },
  'Data Planejada': {
    descriptor: 'Em minutos',
    unit: 'Min',
    help: 'Data Planejada: Iniciar a viagem dentro de uma tolerância permitida. Medido em minutos.',
  },
};

const GENERIC_RULE: VariableRule = { descriptor: '', unit: '', help: '' };

function parseDateString(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return new Date(dateStr);
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

const onlyDigits = (v: string): number => Number(v.replace(/[^0-9]/g, ''));

const EditForm: React.FC<EditFormProps> = ({
  form,
  onFormChange,
  criterionOptions,
  pillarOptions,
  variableOptions,
  onBack,
  onSubmit
}) => {
  const [error, setError] = useState<string | null>(null);

  const selectedVariableName = useMemo(() => {
    const opt = variableOptions.find(o => String(o.value) === String(form.variableId));
    return opt?.label ?? '';
  }, [variableOptions, form.variableId]);

  const rule = VARIABLE_RULES[selectedVariableName] ?? GENERIC_RULE;
  const isBetween = form.criterion === ECriterion.BETWEEN;
  const valueLabel = rule.descriptor ? `Valor (${rule.descriptor})` : 'Valor';
  const fromLabel = rule.descriptor ? `De (${rule.descriptor})` : 'De';
  const toLabel = rule.descriptor ? `Até (${rule.descriptor})` : 'Até';

  const validate = () => {
    if (!form.name.trim()) return 'Nome é obrigatório';
    if (!form.description.trim()) return 'Descrição é obrigatória';
    if (!form.criterion) return 'Critério é obrigatório';
    if (!form.pillar) return 'Pilar é obrigatório';
    if (!form.variableId || form.variableId <= 0) return 'Variável é obrigatória';
    if (!form.points || isNaN(form.points) || form.points <= 0) return 'Pontos devem ser maiores que zero';
    if (!form.validUntil) return 'Data de validade é obrigatória';
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onSubmit();
  };

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  return (
    <form className="display-flex flex-column" onSubmit={handleSubmit}>
      <div className="display-flex flex-column width-100pct">
        <div className="margin-bottom-5">
          <h3 className="text-size-18 text-bold text-color-info margin-bottom-3">Configurações básicas</h3>
          <span className="text-color-dark text-size-14">Defina as informações básicas para esse desafio</span>
        </div>

        <div className="display-flex flex-row gap-20 margin-top-15 align-items-end">
          <CustomInput
            label="Nome"
            id="challengeName"
            value={form.name}
            onChange={v => onFormChange('name', v)}
            maxLength={150}
            placeholder="Digite aqui..."
            className="width-50pct"
          />
          <CustomInput
            label="Válido Até"
            id="challengeValidUntil"
            value={formatDateForInput(form.validUntil)}
            onChange={v => onFormChange('validUntil', v)}
            type="date"
            className="width-20pct"
          />
          <CustomInput
            label="Pontos obtidos"
            id="challengePoints"
            value={form.points}
            onChange={v => onFormChange('points', onlyDigits(v))}
            type="text"
            placeholder="Ex: 100, 200, 300"
            className="width-20pct"
          />
          <CustomSwitch
            label="Execução Única"
            checked={form.uniqueExecute}
            onChange={v => onFormChange('uniqueExecute', v)}
          />
          <div
            className="border border-style-solid border-color-light border-width-1 border-left-only border-top-none border-right-none border-bottom-none height-40 flex-shrink-0"
            aria-hidden="true"
          />
          <CustomSwitch
            label="Desafio ativo"
            checked={form.active}
            onChange={v => onFormChange('active', v)}
          />
        </div>

        <CustomTextarea
          label="Descrição"
          id="challengeDescription"
          value={form.description}
          onChange={v => onFormChange('description', v)}
          maxLength={250}
          rows={3}
          placeholder="Digite aqui..."
          className="width-100pct margin-top-15"
        />
      </div>

      <div className="border border-style-solid border-color-light border-width-1 border-top-only margin-top-25 padding-top-20">
        <div className="margin-bottom-5">
          <h3 className="text-size-18 text-bold text-color-info margin-bottom-3">Configurações de regras</h3>
          <span className="text-color-dark text-size-14">Defina as regras e parâmetros para o desafio ser considerado como concluído com sucesso</span>
        </div>

        <div className="display-flex flex-row gap-20 margin-top-15 align-items-end">
          <CustomSelect
            label="Variável"
            id="variableId"
            value={form.variableId.toString()}
            onChange={v => onFormChange('variableId', onlyDigits(v))}
            options={variableOptions}
            className="width-20pct"
          />
          <CustomSelect
            label="Critério"
            id="challengeCriterion"
            value={form.criterion}
            onChange={v => onFormChange('criterion', v)}
            options={criterionOptions}
            className="width-20pct"
          />
          {isBetween ? (
            <>
              <CustomInput
                label={fromLabel}
                id="challengeCriterionValue"
                value={form.criterionValue}
                onChange={v => onFormChange('criterionValue', onlyDigits(v))}
                type="text"
                unit={rule.unit}
                className="width-15pct"
              />
              <CustomInput
                label={toLabel}
                id="challengeCriterionSecondValue"
                value={form.criterionSecondValue}
                onChange={v => onFormChange('criterionSecondValue', onlyDigits(v))}
                type="text"
                unit={rule.unit}
                className="width-15pct"
              />
            </>
          ) : (
            <CustomInput
              label={valueLabel}
              id="challengeCriterionValue"
              value={form.criterionValue}
              onChange={v => onFormChange('criterionValue', onlyDigits(v))}
              type="text"
              unit={rule.unit}
              className="width-20pct"
            />
          )}
          <CustomSelect
            label="Pilar"
            id="challengePillar"
            value={form.pillar}
            onChange={v => onFormChange('pillar', v)}
            options={pillarOptions}
            className="width-20pct"
          />
        </div>

        {rule.help && (
          <div className="bg-lightest rounded padding-10 margin-top-15 display-flex align-items-center">
            <span className="rioglyph rioglyph-info-sign text-color-info text-size-18 margin-right-10" aria-hidden="true" />
            <span className="text-size-14 text-color-dark">{rule.help}</span>
          </div>
        )}
      </div>

      <div className="padding-y-20">
        {error && <div className="alert alert-danger">{error}</div>}
      </div>
      <div className="display-flex gap-10 justify-content-end margin-top-24">
        <button type="button" className="btn btn-default" onClick={onBack}>Voltar</button>
        <button type="submit" className="btn btn-primary">Salvar desafio</button>
      </div>
    </form>
  );
};

export { parseDateString };
export default EditForm;
